import React, { useEffect, useMemo, useState } from 'react';
import { packageAPI, formsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { proratedGB } from '../utils/prorate';

// Utility function to format numbers with commas
const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

const Packages: React.FC = () => {
  const [packages, setPackages] = useState<{ id: number; capGB: number }[]>([]);
  const [forms, setForms] = useState<any[]>([]);
  const [cap, setCap] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [packagesData, formsData] = await Promise.all([
        packageAPI.list(),
        formsAPI.getAllForms().catch(() => []) // Handle API errors gracefully
      ]);
      // Handle paginated response structure: { data: Package[], pagination: {...} }
      const packagesArray = packagesData && Array.isArray(packagesData) 
        ? packagesData 
        : (packagesData?.data && Array.isArray(packagesData.data) ? packagesData.data : []);
      setPackages(packagesArray);
      // Handle paginated response structure: { data: ServiceForm[], pagination: {...} }
      const formsArray = formsData && Array.isArray(formsData) 
        ? formsData 
        : (formsData?.data && Array.isArray(formsData.data) ? formsData.data : []);
      setForms(formsArray);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { load(); }, []);

  const add = async () => {
    const n = Number(cap);
    if (!n || n <= 0) {
      toast.error('Please enter a valid package size');
      return;
    }
    try {
      await packageAPI.create(n);
      toast.success('Package created successfully! 📦');
      setCap('');
      load();
    } catch (error) {
      toast.error('Failed to create package');
    }
  };

  const del = async (id: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this package?');
    if (!confirmed) return;
    try {
      await packageAPI.remove(id);
      toast.success('Package deleted successfully');
      load();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  // Calculate metrics
  const customersWithCappedPackages = forms.filter(form => {
    // Check if form's package limit matches any capped package exactly
    if (packages.some(pkg => pkg.capGB === form.packageLimit)) {
      return true;
    }
    
    // Check if form's package limit is close to a prorated amount of any capped package
    return packages.some(pkg => {
      const proratedAmount = proratedGB(pkg.capGB);
      return Math.abs(form.packageLimit - proratedAmount) < 0.1;
    });
  }).length;

  const totalProratedData = forms.reduce((total, form) => {
    // Check if this form has a capped package (exact match)
    const exactPackage = packages.find(pkg => pkg.capGB === form.packageLimit);
    if (exactPackage) {
      return total + form.packageLimit;
    }
    
    // Check if this form has a prorated capped package
    const proratedPackage = packages.find(pkg => {
      const proratedAmount = proratedGB(pkg.capGB);
      return Math.abs(form.packageLimit - proratedAmount) < 0.1;
    });
    
    if (proratedPackage) {
      return total + form.packageLimit;
    }
    
    return total;
  }, 0);

  const sortedPackages = useMemo(() => {
    return [...packages].sort((a, b) => a.capGB - b.capGB);
  }, [packages]);

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(56,189,248,0.35),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(167,139,250,0.28),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className={`h-2 w-2 rounded-full ${loading ? 'bg-amber-400' : 'bg-emerald-400'}`} />
              {loading ? 'Loading…' : `${packages.length} active packages`}
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Packages</h1>
            <p className="max-w-2xl text-white/70">
              Create and manage capped data packages. We use these to identify capped and prorated allocations on service forms.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {new Date().toLocaleTimeString()}
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-sliders-h text-white/60"></i>
                Capped matching + prorate check
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-rotate-right"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="group rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600">Total packages</p>
              <div className="mt-2 text-3xl font-black text-gray-900">
                {loading ? <div className="animate-pulse bg-gray-200 h-8 w-16 rounded" /> : formatNumber(packages.length)}
              </div>
              <p className="mt-2 text-sm text-gray-500">Active package definitions</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
              <i className="fas fa-box text-xl"></i>
            </div>
          </div>
        </div>

        <div className="group rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600">Customers capped/prorated</p>
              <div className="mt-2 text-3xl font-black text-gray-900">
                {loading ? <div className="animate-pulse bg-gray-200 h-8 w-16 rounded" /> : formatNumber(customersWithCappedPackages)}
              </div>
              <p className="mt-2 text-sm text-gray-500">Matches against forms</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
              <i className="fas fa-users text-xl"></i>
            </div>
          </div>
        </div>

        <div className="group rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-600">Total matched GB</p>
              <div className="mt-2 text-3xl font-black text-gray-900">
                {loading ? <div className="animate-pulse bg-gray-200 h-8 w-20 rounded" /> : formatNumber(Number(totalProratedData.toFixed(1)))}
              </div>
              <p className="mt-2 text-sm text-gray-500">Across capped/prorated forms</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
              <i className="fas fa-chart-pie text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Create */}
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-gray-900">Create package</h2>
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
              <i className="fas fa-plus"></i>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-600">Add a new capped package size in GB.</p>

          <div className="mt-6 space-y-3">
            <label className="block text-sm font-semibold text-gray-700">Package size (GB)</label>
            <div className="relative">
              <input
                value={cap}
                onChange={e => setCap(e.target.value)}
                type="number"
                placeholder="e.g. 100"
                className="w-full rounded-2xl bg-gray-50 px-4 py-3.5 pr-14 text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                min="1"
                step="1"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <span className="text-gray-400 font-semibold">GB</span>
              </div>
            </div>
            <button
              onClick={add}
              disabled={!cap || Number(cap) <= 0}
              className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 font-bold text-white shadow-lg transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-plus mr-2"></i>
              Create
            </button>
            <p className="text-xs text-gray-500">Tip: common values are 10–500GB. Minimum is 1GB.</p>
          </div>
        </div>

        {/* List */}
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-black text-gray-900">Available packages</h2>
              {!loading && packages.length > 0 && (
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                  {packages.length} total
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">Sorted by size</div>
          </div>

          <div className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
                <div className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
                <div className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
              </div>
            ) : packages.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
                  <i className="fas fa-box"></i>
                </div>
                <div className="mt-3 text-lg font-bold text-gray-900">No packages yet</div>
                <div className="mt-1 text-sm text-gray-600">Create your first package to start matching capped allocations.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sortedPackages.map(p => (
                  <div
                    key={p.id}
                    className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 [background:radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.10),transparent_40%)]" />
                    <div className="relative flex items-start justify-between gap-3">
                      <div>
                        <div className="text-2xl font-black text-gray-900">{p.capGB} GB</div>
                        <div className="mt-1 text-sm text-gray-600">Monthly allowance</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700">
                          <i className="fas fa-database"></i>
                        </div>
                        <button
                          onClick={() => del(p.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-50 text-rose-700 hover:bg-rose-100"
                          title="Delete package"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>

                    <div className="relative mt-4">
                      <div className="text-xs font-semibold text-gray-500">Prorated reference</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">
                        ~ {proratedGB(p.capGB).toFixed(1)} GB (this month)
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages; 